// The wall tool (issue 1144, slice 1; design/30 §1.1, §1.2): the DM's mode in which each click on the board places a
// corner of a chain of walls — snapped to the grid's corners when the grid is on and Shift is not held — and a
// double-click or Enter ends the chain as ONE `wall.add`; Escape drops the chain being drawn. A click on a wall, with
// no chain in progress, selects it for the panel (`WallComposer`). One board mode at a time, like the fog tool: starting
// this stops pointing, measuring, areas, the fog and drawing, and each of those stops this. Held outside React; the
// canvas feeds it, the board's `WallGate` sends what it commits through the table store.
//
// Placed lights (issue 1145; design/30 §1.4) are the same tool's other mode, `lights`: a click on the board places a
// light (the design's torch radii, changed in the panel), a click on a light picks it, and with one picked "Move" makes
// the next click its new place. The DM's alone, like the walls.
import { TORCH, distanceToSegment, isGridless, type Extent, type Grid, type LightBody, type PlacedLight, type Wall, type WallBody, type WallPoint } from '@dragons-table/vtt-shared';
import { focusTool } from './focusTool';
import { measureTool } from './measureTool';
import { targetTool } from './targetTool';
import { areaTool } from './areaTool';
import { fogTool } from './fogTool';
import { zoneTool } from './zoneTool';
import { drawTool } from './drawTool';
import { intersectionAt, nearestIntersection } from './areaGeometry';
import { whatsThisTool } from './whatsThisTool';
import { floorView } from './floorView';

export interface WallDraft {
  on: boolean;
  /** The corners placed so far in the chain being drawn. */
  chain: WallPoint[];
  /** Where the next corner would go, under the hand: the rubber band from the last corner. */
  hover: WallPoint | null;
  /** The wall the panel acts on. */
  selected: string | null;
  /** Placing lights instead of walls (issue 1145); the picked light; whether the next click moves it. */
  lights: boolean;
  light: string | null;
  moving: boolean;
}

/** How near (in screen pixels) a click must be to a wall to select it. */
export const WALL_PICK_PX = 6;
/** A corner this near the last one (board pixels) is the same corner: a double-click's second press adds nothing. */
const SAME_CORNER_PX = 3;

/** Where a click puts a corner: the nearest grid corner with the grid on and Shift up, else the point itself (to half a pixel). */
export function wallCorner(grid: Grid, extent: Extent, p: WallPoint, free: boolean): WallPoint {
  const at = free || isGridless(grid) ? { x: p.x, y: p.y } : intersectionAt(grid, extent, nearestIntersection(grid, extent, p));
  return { x: Math.round(Math.max(0, Math.min(extent.width, at.x)) * 2) / 2, y: Math.round(Math.max(0, Math.min(extent.height, at.y)) * 2) / 2 };
}

/** A chain's walls: one a pair of neighbouring corners, both switches on. */
export function chainWalls(points: readonly WallPoint[], newId: () => string = () => crypto.randomUUID()): Wall[] {
  const out: Wall[] = [];
  for (let k = 1; k < points.length; k += 1) {
    const a = points[k - 1]!; const b = points[k]!;
    if (a.x !== b.x || a.y !== b.y) out.push({ id: newId(), a: { ...a }, b: { ...b }, move: true, sight: true });
  }
  return out;
}

/** The placed light nearest a board point within `tolerance` board pixels, or null. */
export function lightAt(lights: readonly PlacedLight[], p: WallPoint, tolerance: number): PlacedLight | null {
  let best: PlacedLight | null = null; let bestD = tolerance;
  for (const l of lights) { const d = Math.hypot(l.x - p.x, l.y - p.y); if (d <= bestD) { best = l; bestD = d; } }
  return best;
}

/**
 * The wall nearest a board point within `tolerance` board pixels, or null. A click at a wall's END is not a pick of it:
 * walls meet at their ends, and a DM drawing the next wall starts it there (the door spec could not draw a door
 * between two walls, 2026-10-10), so a point within `tolerance` of any wall's end picks nothing and starts a chain.
 */
export function wallAt(walls: readonly Wall[], p: WallPoint, tolerance: number): Wall | null {
  const near = (q: WallPoint) => Math.hypot(p.x - q.x, p.y - q.y) <= tolerance;
  if (walls.some((w) => near(w.a) || near(w.b))) return null;
  let best: Wall | null = null; let bestD = tolerance;
  for (const w of walls) { const d = distanceToSegment(p, w.a, w.b); if (d <= bestD) { best = w; bestD = d; } }
  return best;
}

/**
 * A door on the board (issue 1148; design/30 §1.2): a marker at its midpoint, clicked outside any tool as a prop is.
 * The DM's carries the wall's id; a player's is a door as their group last saw it (design/30 §4), with no id.
 */
export interface DoorMark { id: string | null; x: number; y: number; open: boolean }
/** How near (in screen pixels) a click must be to a door's marker. */
export const DOOR_PICK_PX = 12;
export const ASK_TO_OPEN = 'Ask the DM to open it.';
export const ASK_TO_CLOSE = 'Ask the DM to close it.';

/** The doors among the walls a store holds — the DM's whole walls, or a player's as last seen. */
export function doorMarks(walls: ReadonlyArray<{ id?: string; a: WallPoint; b: WallPoint; door?: { open: boolean } }>): DoorMark[] {
  return walls.filter((w) => w.door).map((w) => ({ id: w.id ?? null, x: (w.a.x + w.b.x) / 2, y: (w.a.y + w.b.y) / 2, open: w.door!.open }));
}

/** The door marker nearest a board point within `tolerance` board pixels, or null. */
export function doorAt(marks: readonly DoorMark[], p: WallPoint, tolerance: number): DoorMark | null {
  let best: DoorMark | null = null; let bestD = tolerance;
  for (const m of marks) { const d = Math.hypot(m.x - p.x, m.y - p.y); if (d <= bestD) { best = m; bestD = d; } }
  return best;
}

/**
 * What a click on a door does (issue 1148, decided: only the DM opens and closes doors; players ask). The DM's opens
 * or closes it, one `door.set`; anyone else's sends nothing and is told to ask the DM — by voice or chat, no request.
 */
export function doorClick(mark: DoorMark, isDm: boolean): { body: WallBody } | { say: string } {
  if (isDm && mark.id) return { body: { kind: 'door.set', id: mark.id, open: !mark.open } };
  return { say: mark.open ? ASK_TO_CLOSE : ASK_TO_OPEN };
}

type Listener = (d: WallDraft) => void;
type ToolBody = WallBody | LightBody;
let draft: WallDraft = { on: false, chain: [], hover: null, selected: null, lights: false, light: null, moving: false };
const listeners = new Set<Listener>();
const committers = new Set<(c: ToolBody) => void>();
const set = (next: WallDraft) => { draft = next; for (const l of listeners) l(draft); };
const send = (c: ToolBody) => { for (const fn of committers) fn(c); };

export const wallTool = {
  get: () => draft,
  subscribe(listener: Listener): () => void { listeners.add(listener); return () => { listeners.delete(listener); }; },
  /** Who sends a wall event: the board's gate. */
  onCommit(fn: (c: ToolBody) => void): () => void { committers.add(fn); return () => { committers.delete(fn); }; },
  start(): void { whatsThisTool.stop(); focusTool.stop(); measureTool.stop(); areaTool.stop(); targetTool.stop(); fogTool.stop(); drawTool.stop(); zoneTool.stop(); set({ on: true, chain: [], hover: null, selected: null, lights: false, light: null, moving: false }); },
  stop(): void { if (draft.on || draft.chain.length || draft.selected) set({ on: false, chain: [], hover: null, selected: null, lights: false, light: null, moving: false }); },
  toggle(): void { if (draft.on) wallTool.stop(); else wallTool.start(); },
  /** A click: a corner of the chain (the selection, if any, is let go). */
  corner(p: WallPoint): void {
    if (!draft.on) return;
    const last = draft.chain[draft.chain.length - 1];
    if (last && Math.hypot(last.x - p.x, last.y - p.y) < SAME_CORNER_PX) return;
    set({ ...draft, chain: [...draft.chain, { ...p }], selected: null });
  },
  hover(p: WallPoint | null): void { if (draft.on && (draft.hover?.x !== p?.x || draft.hover?.y !== p?.y)) set({ ...draft, hover: p }); },
  /** Double-click or Enter: the chain becomes one `wall.add`; a single corner is no wall. */
  finish(): void {
    if (!draft.on) return;
    const walls = chainWalls(draft.chain);
    set({ ...draft, chain: [], hover: null });
    if (walls.length) send({ kind: 'wall.add', walls });
  },
  /** Escape: the chain in progress goes; nothing is sent. */
  cancel(): void { if (draft.chain.length || draft.hover) set({ ...draft, chain: [], hover: null }); },
  select(id: string | null): void { if (draft.on && draft.selected !== id) set({ ...draft, chain: [], hover: null, selected: id }); },
  /** Walls or lights (issue 1145): switching drops the chain and any pick. */
  mode(lights: boolean): void { if (draft.on && draft.lights !== lights) set({ ...draft, lights, chain: [], hover: null, selected: null, light: null, moving: false }); },
  pickLight(id: string | null): void { if (draft.on && (draft.light !== id || draft.moving)) set({ ...draft, light: id, moving: false }); },
  /** "Move": the next click on the board is the picked light's new place. */
  moveLight(): void { if (draft.on && draft.light) set({ ...draft, moving: true }); },
  /**
   * A click in the lights mode, given the lights on the scene and how near (board pixels) counts as on one: moving, the
   * picked light goes there; on a light, it is picked; elsewhere a new light is placed there (the design's torch) and picked.
   */
  lightClick(p: WallPoint, lights: readonly PlacedLight[], tolerance: number, newId: () => string = () => crypto.randomUUID()): void {
    if (!draft.on || !draft.lights) return;
    const at = { x: Math.round(p.x * 2) / 2, y: Math.round(p.y * 2) / 2 };
    const picked = draft.light ? lights.find((l) => l.id === draft.light) : undefined;
    if (draft.moving && picked) { set({ ...draft, moving: false }); send({ kind: 'light.set', light: { ...picked, ...at } }); return; }
    const hit = lightAt(lights, p, tolerance);
    if (hit) { set({ ...draft, light: hit.id, moving: false }); return; }
    const id = newId();
    set({ ...draft, light: id, moving: false });
    // On the floor in view (qa-runs#1160 slice B): a light lights its own floor; none picked is the ground floor.
    const floor = floorView.get();
    send({ kind: 'light.add', light: { id, ...at, ...TORCH, ...(floor ? { floor } : {}) } });
  },
  /** The panel's changes: sent as they are. */
  send,
};
